import { defineComponent } from "vue";

import { COMPONENT_NAME } from "./attributes";

import { SharedSelect } from "@/components/shared";
import { UIInput, UIButton } from "@/components/ui";

import { DepositWithdrawalStoreRequestFactory } from "@/shared/repository/modules/deposit-withdrawal/factory";
import { SharedSelectOption } from "@/components/shared/select/model";
import { RequisitesBankResponse } from "@/shared/repository/modules/requisites/repo";
import { AdminCurrencyShowResponse } from "@/shared/repository/modules/admin/currency/repo";
import { AdminCurrencyType } from "@/shared/repository/modules/admin/currency/enums";
import { SelectOptionFactory } from "@/shared/factory/select";
import { AdminApplicationType } from "@/shared/repository/modules/admin/application/enums";
import { WalletModalFactory } from "@/shared/repository/modules/wallet/factory";
import { WalletWithAmountResponse } from "@/shared/repository/modules/wallet/repo";
import { ModalFactory } from "@/shared/factory/modal";
import { formatFiatCurrency } from "@/shared/utils/amount-helpers";
import { DepositWithdrawalShowResponse } from "@/shared/repository/modules/deposit-withdrawal/repo";
import {
  ModalIcon,
  ModalLabel,
  ModalTitle,
} from "@/shared/constants/constants";

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    SharedSelect,
    UIInput,
    UIButton,
  },
  data() {
    return {
      isLoading: false,
      isCurrencyDisabled: false,
      currency: <AdminCurrencyShowResponse[]>[],
      requisites: <RequisitesBankResponse[]>[],
      depositWithdrawalStore: DepositWithdrawalStoreRequestFactory({
        user_id: this.$projectServices.userRepo.userInfo.id,
        currency_id: this.$projectServices.depositWithdrawalRepo.application
          ? this.$projectServices.depositWithdrawalRepo.application.currency_id
          : 0,
        amount: this.$projectServices.depositWithdrawalRepo.application
          ? this.$projectServices.depositWithdrawalRepo.application.amount
          : 0,
      }),
      modalRepo: this.$projectServices.modalRepo,
      walletRepo: this.$projectServices.walletRepo,
      depositWithdrawalRepo: this.$projectServices.depositWithdrawalRepo,
      walletRepository: this.$projectServices.walletRepository,
      requisitesRepository: this.$projectServices.requisitesRepository,
      depositWithdrawalRepository:
        this.$projectServices.depositWithdrawalRepository,
      adminCommissionRepository:
        this.$projectServices.adminCommissionRepository,
      userRepo: this.$projectServices.userRepo,
      commissionPercent: <number>0,
    };
  },
  emits: {
    update: null,
  },
  async created(): Promise<void> {
    try {
      this.isLoading = true;

      if (this.walletRepo.modal.isDisabled) {
        const { currencyId, amount } = this.walletRepo.modal;

        this.depositWithdrawalStore = DepositWithdrawalStoreRequestFactory({
          user_id: this.$projectServices.userRepo.userInfo.id,
          currency_id: currencyId,
          amount,
        });

        this.isCurrencyDisabled = true;
      }

      const commissionRequest = {
        user_id: this.userRepo.userInfo.id,
        currency_type: AdminCurrencyType.fiat,
        app_type:
          this.walletRepo.modal.type == AdminApplicationType.deposit
            ? AdminApplicationType.deposit
            : AdminApplicationType.withdrawal,
      };

      const [currency, requisites, requisitesAdmin, commission] =
        await Promise.all([
          this.walletRepository.currency(),
          this.requisitesRepository.bankList(this.userRepo.userInfo.id),
          this.adminCommissionRepository.requisitesList(),
          this.depositWithdrawalRepository.commission(commissionRequest),
        ]);

      this.commissionPercent = commission.commission;

      this.currency = currency.filter(
        (item) =>
          item.type === AdminCurrencyType.fiat &&
          !item.frozen &&
          requisitesAdmin.find((requisite) => requisite.currency.id === item.id)
      );
      this.requisites = requisites;
      if (this.walletRepo.modal.bankId) {
        const requisite = requisites.find(
          (item) => item.id === this.walletRepo.modal.bankId
        );
        if (requisite) {
          this.depositWithdrawalStore.user_requisite_id = requisite.id;
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.isLoading = false;
    }
  },
  beforeUnmount(): void {
    this.depositWithdrawalRepo.CLEAR_APPLICATION();
  },
  computed: {
    displayedRequisiteOptions(): SharedSelectOption[] {
      return this.requisites.map((requisite) =>
        SelectOptionFactory(
          {
            id: requisite.id,
            label: requisite.name,
            value: requisite.id,
          },
          this.depositWithdrawalStore.user_requisite_id
        )
      );
    },

    displayedWalletWithAmount(): WalletWithAmountResponse[] {
      return this.walletRepo.withAmount;
    },

    isWithdrawal(): boolean {
      return this.walletRepo.modal.type === AdminApplicationType.withdrawal;
    },

    displayedCurrentWalletWithAmount(): WalletWithAmountResponse | undefined {
      return this.displayedWalletWithAmount.find((value) => {
        return value.currency_id === this.depositWithdrawalStore.currency_id;
      });
    },

    displayedCurrencyOptions(): SharedSelectOption[] {
      return this.currency.map((currency) =>
        SelectOptionFactory(
          {
            id: currency.id,
            label: currency.name.toUpperCase(),
            value: currency.id,
            icon: currency.country,
          },
          this.depositWithdrawalStore.currency_id
        )
      );
    },

    displayedCurrencyName(): string {
      return (
        this.currency
          .find(
            (currency) =>
              currency.id === this.depositWithdrawalStore.currency_id
          )
          ?.name.toUpperCase() ?? ""
      );
    },

    displayedCurrencyIcon(): string {
      return (
        this.currency.find(
          (currency) => currency.id === this.depositWithdrawalStore.currency_id
        )?.country ?? ""
      );
    },

    isDisabled(): boolean {
      const { user_requisite_id, currency_id, amount } =
        this.depositWithdrawalStore;

      return !user_requisite_id || !currency_id || !amount || this.isLoading;
    },
    isCommissionsShow(): boolean {
      if (
        this.depositWithdrawalStore.currency_id &&
        this.depositWithdrawalStore.amount &&
        this.depositWithdrawalStore.amount > 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    displayedCommission(): string {
      return formatFiatCurrency(
        0.01 *
          this.depositWithdrawalStore.amount *
          this.commissionPercent.valueOf()
      );
    },
    displayedAmount(): string {
      return formatFiatCurrency(
        this.depositWithdrawalStore.amount -
          0.01 *
            this.depositWithdrawalStore.amount *
            this.commissionPercent.valueOf()
      );
    },
  },
  methods: {
    async handleCreate(): Promise<void> {
      this.isLoading = true;
      try {
        const { type } = this.walletRepo.modal;

        const depositWithdrawal = await this.depositWithdrawalRepository.store(
          type as AdminApplicationType,
          AdminCurrencyType.fiat,
          this.depositWithdrawalStore
        );

        if (type === AdminApplicationType.deposit) {
          this.handleDepositSuccess(depositWithdrawal);
        } else if (type === AdminApplicationType.withdrawal) {
          this.handleWithdrawalSuccess();
        } else {
          this.handleOtherSuccess();
        }
      } catch (error) {
        console.error("handleCreate error:", error);
      } finally {
        this.$emit("update");
        this.isLoading = false;
      }
    },

    handleDepositSuccess(
      depositWithdrawal: DepositWithdrawalShowResponse
    ): void {
      this.walletRepo.UPDATE_MODAL(WalletModalFactory());

      const currencyId =
        depositWithdrawal.currency_id ||
        this.depositWithdrawalStore.currency_id;

      const currencyName =
        this.currency.find((item) => item.id === currencyId)?.name ?? "";

      this.depositWithdrawalRepo.UPDATE_DEPOSIT({
        id: depositWithdrawal.id || Date.now(), // TODO: remove Date.now()
        currency_id: currencyId,
        amount: depositWithdrawal.amount || this.depositWithdrawalStore.amount,
        currencyName,
      });
    },

    handleWithdrawalSuccess(): void {
      this.walletRepo.UPDATE_MODAL(
        WalletModalFactory({
          type: AdminApplicationType.verificationWithdrawal,
        })
      );
    },

    handleOtherSuccess(): void {
      this.walletRepo.UPDATE_MODAL(WalletModalFactory());
      this.modalRepo.UPDATE_MODAL(
        ModalFactory({
          title: ModalTitle.application,
          label: ModalLabel.applicationWithdrawal,
          icon: ModalIcon.application,
          callback: () => {},
        })
      );
    },
  },
});
